import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { ProgressArc } from '../../../core/components/ProgressArc';
import { selectCostPerCarResults } from '../reducer';
import { selectCoreSiteId } from '../../settings/reducer';
import { FETCH_COST_PER_CAR_DATA, fetchCostPerCarData } from '../actions';
import { selectApiRequests } from '../../../store/apiRequestReducer';
import Loader from '../../../core/components/Loader';
import { OVERVIEW_YELLOW } from '../containers/Charts';

interface ArcData {
  label: string;
  color: string;
  value: number;
  min: number;
  max: number;
  format: 'currency' | 'percent';
}

export const CostPerCarArcs = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const costPerCarData = useSelector(selectCostPerCarResults),
    coreSiteId = useSelector(selectCoreSiteId);
  const cpcDataLoading = useSelector((state) => {
    const req = selectApiRequests(state)[FETCH_COST_PER_CAR_DATA];
    return !req || req.isLoading;
  });

  const handleCostPerCarData = () => {
    if (coreSiteId) {
      dispatch(fetchCostPerCarData());
    }
  };
  useEffect(handleCostPerCarData, [coreSiteId]);

  const data: ArcData[] = useMemo(() => {
    return [
      {
        label: intl.formatMessage({ id: 'water' }),
        color: '#3b82f6',
        value: costPerCarData?.water ?? 0,
        min: 0.1,
        max: 0.9,
        format: 'currency',
      },
      {
        label: intl.formatMessage({ id: 'electric' }),
        color: OVERVIEW_YELLOW,
        value: costPerCarData?.electric ?? 0,
        min: 0.1,
        max: 0.7,
        format: 'currency',
      },
      {
        label: intl.formatMessage({ id: 'gas' }),
        color: '#EF4345',
        value: costPerCarData?.gas ?? 0,
        min: 0.02,
        max: 0.3,
        format: 'currency',
      },
      {
        label: intl.formatMessage({ id: 'claims' }),
        color: '#8b5cf6',
        value: costPerCarData?.claims ?? 0,
        min: 0.01,
        max: 0.2,
        format: 'currency',
      },
      {
        label: intl.formatMessage({ id: 'labor' }),
        color: '#06b6d4',
        value: costPerCarData?.labor ?? 0,
        min: 1,
        max: 8,
        format: 'percent',
      },
      {
        label: intl.formatMessage({ id: 'detergents' }),
        color: '#10b981',
        value: costPerCarData?.detergents ?? 0,
        min: 0.1,
        max: 1.1,
        format: 'currency',
      },
    ];
  }, [costPerCarData]);

  const formatValue = (value: number, format: string) => {
    return format === 'currency' ? `$${value.toFixed(2)}` : `${value.toFixed(1)}%`;
  };

  return (
    <section className="overview-chart-card vertically-centered">
      <p className="chart-title">{intl.formatMessage({ id: 'yourCostPerCar' })}</p>
      {cpcDataLoading ? (
        <Loader />
      ) : (
        <div className="cpc-arcs">
          {data.map((a: ArcData, i: number) => (
            <div className="cpc-arc" key={i}>
              <span className="cpc-top-arc-label">
                {formatValue(a.min + (a.max - a.min) / 2, a.format)}
              </span>
              <ProgressArc
                value={a.value}
                minValue={a.min}
                maxValue={a.max}
                color={a.color}
                backgroundColor="#E5E6EA"
                arcLength={180}
                arcStart={270}
                subdivisions={2}
                subdivisionColor="rgba(0,0,0,0.4)"
                labelPosition="bottom"
                formatLabel={(v) => formatValue(v, a.format)}
              />
              <span className="cpc-bottom-arc-label">{a.label}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
